import React from "react"
import autobind from "class-autobind"
import { Paper, Toggle, Tabs, Tab } from "material-ui"
import { browserHistory } from "react-router"
import DashboardMap from "./DashboardMap"
import HierarchySettingsStore from "../../stores/HierarchySettingsStore"
import * as api from "../../api"
import { enableIntercom } from "../../../intercom"
import moment from "moment"
import { IPunchMapData, IDate, IDashboardChartData, IDashboardMapChartData, EDashboardDetailType } from "../../models"
import DonutChart from "../../components/common/donutChart/DonutChart"
import strings from "../../localization/strings"
import { blue300, blue700, orange300, orange700, red300, red700 } from "material-ui/styles/colors"
import DashboardDetail from "./DashboardDetail"
import DashboardBarChartDrawer from "./DashboardBarChartDrawer"
import * as utils from "../../helpers/utils"
import AccountStore from "../../stores/AccountStore"
import AuthenticationStore from "../../stores/AuthenticationStore"
import * as permissionHelper from "../../helpers/permissionHelper"
import WorkforceAssessment from "./workforce-assessment/WorkforceAssessment"
import * as userHelper from "../../helpers/userHelper"
import * as timeHelper from "../../helpers/timeHelper"
import TrendsDashboard from "./trends/TrendsDashboard"

declare global {
    interface Window {
        Intercom: any
    }
}
interface IState {
    loading: boolean
    dataCallingUTCTime: IDate
    dataCallingClientLocalTime: IDate
    currentClientUTCTime: IDate
    currentClientLocalTime: IDate
    globalHierarchyItemId: string
    filterByPunchPermissionHierarchy: boolean
    filterDirectReportsOnly: boolean
    mapData: IPunchMapData[]
    chartData: IDashboardChartData
    drawerChartType: EDashboardDetailType
    activatedTab: EDashboardDetailType
    showDashboardMap: boolean
    tabActive: string
}

export default class Dashboard extends React.Component<null, IState> {
    private timer
    constructor(props) {
        super(props)
        const storedFilterByPunchPermissionHierarchy = utils.getCookie("DashboardFilterByPunchPermissionHierarchy")
        const storedFilterDirectReportsOnly = utils.getCookie("DashboardFilterDirectReportsOnly")
        this.state = {
            loading: true,
            dataCallingUTCTime: moment().utc().format(),
            dataCallingClientLocalTime: moment().format(),
            currentClientUTCTime: moment().utc().format(),
            currentClientLocalTime: moment().format(),
            globalHierarchyItemId: HierarchySettingsStore.getGlobalLevelItemFilterId(),
            filterByPunchPermissionHierarchy: storedFilterByPunchPermissionHierarchy === "true",
            filterDirectReportsOnly: storedFilterDirectReportsOnly === "true" || (AccountStore.getSupervisorDirectReportEnabled() && !permissionHelper.hasPermission(AuthenticationStore.Permissions.SupervisorDirectReport.ViewAllEmployees)),
            mapData: [],
            chartData: null,
            drawerChartType: null,
            activatedTab: EDashboardDetailType.WorkingEmployee,
            showDashboardMap: true,
            tabActive: localStorage.getItem("findd_dashboard_tab") ? localStorage.getItem("findd_dashboard_tab") : "daily",
        }
        autobind(this)
    }

    componentDidMount() {
        this.setCurrentClientTime()
        document.title = `${strings.Dashboard} | ${strings.Findd_Grandd_Central}`
        this.getData()
        HierarchySettingsStore.on("global_change", this.handleChangeHierarchy)
        setTimeout(() => {
            browserHistory.listen(this.routeChanged)
        }, 0)
        this.setTime()
    }

    componentWillUnmount() {
        HierarchySettingsStore.removeListener("global_change", this.handleChangeHierarchy)
        this.timer &&
            clearInterval(this.timer)
    }

    async setCurrentClientTime() {
        const currentClientUTCTime = moment().utc().format()
        const currentClientLocalTime = await timeHelper.getDateTime(this.state.globalHierarchyItemId, null, currentClientUTCTime)
        this.setState({ currentClientUTCTime, currentClientLocalTime })
    }

    setTime() {
        this.timer = setInterval(async () => {
            this.setCurrentClientTime()
        }, 60000)
    }

    routeChanged() {
        enableIntercom() &&
            window.Intercom("update")
    }

    async handleChangeHierarchy() {
        const globalHierarchyItemId = HierarchySettingsStore.getGlobalLevelItemFilterId()
        const dataCallingUTCTime = moment().utc().format()
        const dataCallingClientLocalTime = await timeHelper.getDateTime(globalHierarchyItemId, null, dataCallingUTCTime)
        this.setState({
            showDashboardMap: false,
            dataCallingUTCTime,
            dataCallingClientLocalTime,
            globalHierarchyItemId
        }, () =>
            this.getData()
        )
    }

    async getData() {
        if (!userHelper.getSupervisorAccessPermissionForHierarchyId(this.state.globalHierarchyItemId))
            return
        const dataCallingUTCTime = this.state.dataCallingUTCTime
        const dataCallingClientLocalTime = await timeHelper.getDateTime(this.state.globalHierarchyItemId, null, dataCallingUTCTime)
        this.setState({ showDashboardMap: true, loading: true, mapData: [] }, () =>
            api.getDashboardMapChartData(this.state.globalHierarchyItemId, this.state.dataCallingUTCTime, this.state.filterDirectReportsOnly, this.state.filterByPunchPermissionHierarchy).then(res => {
                const result = res.data as IDashboardMapChartData
                this.setState({
                    loading: false,
                    mapData: result.MapData,
                    chartData: result.ChartData,
                    dataCallingClientLocalTime
                })
            })
        )
    }

    render() {
        return (
            <div className="page-container">
                <div className="page-container-parent page-paper">
                    {(!AccountStore.getMedicalAccount() && !AccountStore.getTrendsEnabled()) &&
                        <Paper style={{ padding: 10, backgroundColor: "#ffffff" }} >
                            <div style={{ fontSize: 22, fontWeight: "bold", textAlign: "center", marginTop: 20, marginBottom: 20 }}>
                                {(strings.formatString(
                                    strings.Dashboard_For_x,
                                    moment(this.state.currentClientLocalTime).format("dddd, MMMM DD, YYYY")
                                ) as string).toUpperCase()}
                            </div>
                            <div style={{ textAlign: "right", marginTop: -10, display: "flex", justifyContent: "flex-end" }}>
                                <div>{strings.formatString(
                                    strings.Data_as_of_x,
                                    moment(this.state.dataCallingClientLocalTime).format("M/D/YYYY hh:mma")
                                )}</div>
                                <i
                                    className="fa fa-refresh"
                                    style={{ fontSize: 20, marginLeft: 7, cursor: "pointer" }}
                                    onClick={async () => {
                                        if (!this.state.loading) {
                                            const dataCallingUTCTime = moment().utc().format()
                                            const dataCallingClientLocalTime = await timeHelper.getDateTime(this.state.globalHierarchyItemId, null, dataCallingUTCTime)
                                            this.setState({ showDashboardMap: false, dataCallingUTCTime, dataCallingClientLocalTime }, () => this.getData())
                                        }
                                    }}
                                />
                            </div>
                            <div style={{ display: "flex", height: 250, marginTop: 5 }}>
                                <Paper zDepth={1} style={{ flex: 1, height: "100%", position: "relative" }}>
                                    {this.state.showDashboardMap &&
                                        <DashboardMap
                                            records={this.state.mapData}
                                        />
                                    }
                                </Paper>
                                <Paper zDepth={1} style={{ flex: 2, marginLeft: 15 }}>
                                    <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 5, marginRight: 10 }}>
                                        <div style={{ textAlign: "right", width: 300 }}>
                                            {AccountStore.getSupervisorDirectReportEnabled() && permissionHelper.hasPermission(AuthenticationStore.Permissions.SupervisorDirectReport.ViewAllEmployees) &&
                                                <Toggle
                                                    toggled={this.state.filterDirectReportsOnly}
                                                    onToggle={(e, value) => {
                                                        this.setState({ filterDirectReportsOnly: value }, () => this.getData())
                                                        AccountStore.getSupervisorDirectReportEnabled() && localStorage.setItem("findd_DashboardFilterDirectReportsOnly", value)
                                                    }}
                                                    labelPosition="left"
                                                    label={strings.Show_Direct_Reports_Only}
                                                    disabled={this.state.loading}
                                                />
                                            }
                                            <Toggle
                                                toggled={!this.state.filterByPunchPermissionHierarchy}
                                                onToggle={(e, value) => {
                                                    const filterByPunchPermissionHierarchy = !value
                                                    this.setState({ filterByPunchPermissionHierarchy }, () => this.getData())
                                                    localStorage.setItem("findd_DashboardFilterByPunchPermissionHierarchy", filterByPunchPermissionHierarchy as any)
                                                }}
                                                labelPosition="left"
                                                label={strings.Filter_By_Default_Hierarchy}
                                                disabled={this.state.loading}
                                            />
                                        </div>
                                    </div>
                                    <div style={{ display: "flex", paddingTop: 10 }}>
                                        <div style={{ flex: 1 }}>
                                            <DonutChart
                                                hidePercent
                                                value={this.state.loading ? 0 : this.state.chartData.WorkingEmployee * 100 / this.state.chartData.TotalEmployee}
                                                customValueStr={this.state.loading ? strings.loading : this.state.chartData.WorkingEmployee.toString()}
                                                strokeWidth={15}
                                                size={120}
                                                valuelabel={strings.Working.toUpperCase()}
                                                textValStyle={{ fontSize: this.state.loading ? 16 : 20, fontWeight: "bold" }}
                                                textLabelStyle={{ fontSize: 8 }}
                                                spaceBetweenValAndLabel={15}
                                                indicatorStrokeColor={blue300}
                                                trackStrokeColor={blue700}
                                                onClick={() => !this.state.loading && this.state.chartData.HierarchyData.length > 0 &&
                                                    this.setState({ drawerChartType: EDashboardDetailType.WorkingEmployee })
                                                }
                                            />
                                            <div style={{ textAlign: "center", marginTop: 10, color: blue700 }}>
                                                {`${this.state.loading ? "" : this.state.chartData.TotalEmployee} ${strings.Total_Employees.toLowerCase()}`}
                                            </div>
                                        </div>
                                        <div style={{ flex: 1 }}>
                                            <DonutChart
                                                hidePercent
                                                value={this.state.loading ? 0 : this.state.chartData.ApproachingOT * 100 / this.state.chartData.TotalEmployee}
                                                customValueStr={this.state.loading ? strings.loading : this.state.chartData.ApproachingOT.toString()}
                                                strokeWidth={15}
                                                size={120}
                                                valuelabel={strings.Approaching_OT.toUpperCase()}
                                                textValStyle={{ fontSize: this.state.loading ? 16 : 20, fontWeight: "bold" }}
                                                textLabelStyle={{ fontSize: 8 }}
                                                spaceBetweenValAndLabel={15}
                                                indicatorStrokeColor={orange300}
                                                trackStrokeColor={orange700}
                                                onClick={() => !this.state.loading && this.state.chartData.HierarchyData.length > 0 &&
                                                    this.setState({ drawerChartType: EDashboardDetailType.ApproachingOT })
                                                }
                                            />
                                            <div style={{ textAlign: "center", marginTop: 10, color: orange700 }}>
                                                {`${this.state.loading ? "" : this.state.chartData.TotalEmployee} ${strings.Total_Employees.toLowerCase()}`}
                                            </div>
                                        </div>
                                        <div style={{ flex: 1 }}>
                                            <DonutChart
                                                hidePercent
                                                value={this.state.loading ? 0 : this.state.chartData.OT * 100 / this.state.chartData.TotalEmployee}
                                                customValueStr={this.state.loading ? strings.loading : this.state.chartData.OT.toString()}
                                                strokeWidth={15}
                                                size={120}
                                                valuelabel={strings.Overtime.toUpperCase()}
                                                textValStyle={{ fontSize: this.state.loading ? 16 : 20, fontWeight: "bold" }}
                                                textLabelStyle={{ fontSize: 8 }}
                                                spaceBetweenValAndLabel={15}
                                                indicatorStrokeColor={red300}
                                                trackStrokeColor={red700}
                                                onClick={() => !this.state.loading && this.state.chartData.HierarchyData.length > 0 &&
                                                    this.setState({ drawerChartType: EDashboardDetailType.OT })
                                                }
                                            />
                                            <div style={{ textAlign: "center", marginTop: 10, color: red700 }}>
                                                {`${this.state.loading ? "" : this.state.chartData.TotalEmployee} ${strings.Total_Employees.toLowerCase()}`}
                                            </div>
                                        </div>
                                    </div>
                                </Paper>
                            </div>
                            <DashboardDetail
                                utcTime={this.state.dataCallingUTCTime}
                                globalHierarchyItemId={this.state.globalHierarchyItemId}
                                filterByPunchPermissionHierarchy={this.state.filterByPunchPermissionHierarchy}
                                filterDirectReportsOnly={this.state.filterDirectReportsOnly}
                                activatedTab={this.state.activatedTab}
                                handleChangeActivatedTab={activatedTab => this.setState({ activatedTab })}
                            />
                            <DashboardBarChartDrawer
                                open={this.state.drawerChartType !== null}
                                loading={this.state.loading}
                                chartType={this.state.drawerChartType}
                                hierarchyData={this.state.loading ? [] : this.state.chartData.HierarchyData}
                                handleClose={() => this.setState({ drawerChartType: null })}
                                handleChangeActivatedTab={activatedTab => this.setState({ activatedTab })}
                            />
                        </Paper>
                    }
                    {(AccountStore.getMedicalAccount() || AccountStore.getTrendsEnabled()) &&
                        <Tabs
                            value={this.state.tabActive}
                            onChange={tabActive => {
                                this.setState({ tabActive })
                                localStorage.setItem("findd_dashboard_tab", tabActive)
                            }}
                        >
                            <Tab data-intercom-target={"menu-Dashboard-Daily"} label="Daily Dashboard" value="daily">
                                <Paper style={{ padding: 10, backgroundColor: "#ffffff" }} >
                                    <div style={{ fontSize: 22, fontWeight: "bold", textAlign: "center", marginTop: 20, marginBottom: 20 }}>
                                        {`DASHBOARD FOR ${moment(this.state.currentClientLocalTime).format("dddd, MMMM DD, YYYY").toUpperCase()}`}
                                    </div>
                                    <div style={{ textAlign: "right", marginTop: -10, display: "flex", justifyContent: "flex-end" }}>
                                        <div>{`Data as of ${moment(this.state.dataCallingClientLocalTime).format("M/D/YYYY hh:mma")}`}</div>
                                        <i
                                            className="fa fa-refresh"
                                            style={{ fontSize: 20, marginLeft: 7, cursor: "pointer" }}
                                            onClick={async () => {
                                                if (!this.state.loading) {
                                                    const dataCallingUTCTime = moment().utc().format()
                                                    const dataCallingClientLocalTime = await timeHelper.getDateTime(this.state.globalHierarchyItemId, null, dataCallingUTCTime)
                                                    this.setState({ showDashboardMap: false, dataCallingUTCTime, dataCallingClientLocalTime }, () => this.getData())
                                                }
                                            }}
                                        />
                                    </div>
                                    <div style={{ display: "flex", height: 250, marginTop: 5 }}>
                                        <Paper zDepth={1} style={{ flex: 1, height: "100%", position: "relative" }}>
                                            {this.state.showDashboardMap &&
                                                <DashboardMap
                                                    records={this.state.mapData}
                                                />
                                            }
                                        </Paper>
                                        <Paper zDepth={1} style={{ flex: 2, marginLeft: 15 }}>
                                            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 5, marginRight: 10 }}>
                                                <div style={{ textAlign: "right", width: 300 }}>
                                                    {AccountStore.getSupervisorDirectReportEnabled() && permissionHelper.hasPermission(AuthenticationStore.Permissions.SupervisorDirectReport.ViewAllEmployees) &&
                                                        <Toggle
                                                            toggled={this.state.filterDirectReportsOnly}
                                                            onToggle={(e, value) => {
                                                                this.setState({ filterDirectReportsOnly: value }, () => this.getData())
                                                                AccountStore.getSupervisorDirectReportEnabled() && localStorage.setItem("findd_DashboardFilterDirectReportsOnly", value)
                                                            }}
                                                            labelPosition="left"
                                                            label={strings.Show_Direct_Reports_Only}
                                                            disabled={this.state.loading}
                                                        />
                                                    }
                                                    <Toggle
                                                        toggled={!this.state.filterByPunchPermissionHierarchy}
                                                        onToggle={(e, value) => {
                                                            const filterByPunchPermissionHierarchy = !value
                                                            this.setState({ filterByPunchPermissionHierarchy }, () => this.getData())
                                                            localStorage.setItem("findd_DashboardFilterByPunchPermissionHierarchy", filterByPunchPermissionHierarchy as any)
                                                        }}
                                                        labelPosition="left"
                                                        label={strings.Filter_By_Default_Hierarchy}
                                                        disabled={this.state.loading}
                                                    />
                                                </div>
                                            </div>
                                            <div style={{ display: "flex", paddingTop: 10 }}>
                                                <div style={{ flex: 1 }}>
                                                    <DonutChart
                                                        hidePercent
                                                        value={this.state.loading ? 0 : this.state.chartData.WorkingEmployee * 100 / this.state.chartData.TotalEmployee}
                                                        customValueStr={this.state.loading ? strings.loading : this.state.chartData.WorkingEmployee.toString()}
                                                        strokeWidth={15}
                                                        size={120}
                                                        valuelabel={strings.Working.toUpperCase()}
                                                        textValStyle={{ fontSize: this.state.loading ? 16 : 20, fontWeight: "bold" }}
                                                        textLabelStyle={{ fontSize: 8 }}
                                                        spaceBetweenValAndLabel={15}
                                                        indicatorStrokeColor={blue300}
                                                        trackStrokeColor={blue700}
                                                        onClick={() => !this.state.loading && this.state.chartData.HierarchyData.length > 0 &&
                                                            this.setState({ drawerChartType: EDashboardDetailType.WorkingEmployee })
                                                        }
                                                    />
                                                    <div style={{ textAlign: "center", marginTop: 10, color: blue700 }}>
                                                        {`${this.state.loading ? "" : this.state.chartData.TotalEmployee} ${strings.Total_Employees.toLowerCase()}`}
                                                    </div>
                                                </div>
                                                <div style={{ flex: 1 }}>
                                                    <DonutChart
                                                        hidePercent
                                                        value={this.state.loading ? 0 : this.state.chartData.ApproachingOT * 100 / this.state.chartData.TotalEmployee}
                                                        customValueStr={this.state.loading ? strings.loading : this.state.chartData.ApproachingOT.toString()}
                                                        strokeWidth={15}
                                                        size={120}
                                                        valuelabel={strings.Approaching_OT.toUpperCase()}
                                                        textValStyle={{ fontSize: this.state.loading ? 16 : 20, fontWeight: "bold" }}
                                                        textLabelStyle={{ fontSize: 8 }}
                                                        spaceBetweenValAndLabel={15}
                                                        indicatorStrokeColor={orange300}
                                                        trackStrokeColor={orange700}
                                                        onClick={() => !this.state.loading && this.state.chartData.HierarchyData.length > 0 &&
                                                            this.setState({ drawerChartType: EDashboardDetailType.ApproachingOT })
                                                        }
                                                    />
                                                    <div style={{ textAlign: "center", marginTop: 10, color: orange700 }}>
                                                        {`${this.state.loading ? "" : this.state.chartData.TotalEmployee} ${strings.Total_Employees.toLowerCase()}`}
                                                    </div>
                                                </div>
                                                <div style={{ flex: 1 }}>
                                                    <DonutChart
                                                        hidePercent
                                                        value={this.state.loading ? 0 : this.state.chartData.OT * 100 / this.state.chartData.TotalEmployee}
                                                        customValueStr={this.state.loading ? strings.loading : this.state.chartData.OT.toString()}
                                                        strokeWidth={15}
                                                        size={120}
                                                        valuelabel={strings.Overtime.toUpperCase()}
                                                        textValStyle={{ fontSize: this.state.loading ? 16 : 20, fontWeight: "bold" }}
                                                        textLabelStyle={{ fontSize: 8 }}
                                                        spaceBetweenValAndLabel={15}
                                                        indicatorStrokeColor={red300}
                                                        trackStrokeColor={red700}
                                                        onClick={() => !this.state.loading && this.state.chartData.HierarchyData.length > 0 &&
                                                            this.setState({ drawerChartType: EDashboardDetailType.OT })
                                                        }
                                                    />
                                                    <div style={{ textAlign: "center", marginTop: 10, color: red700 }}>
                                                        {`${this.state.loading ? "" : this.state.chartData.TotalEmployee} ${strings.Total_Employees.toLowerCase()}`}
                                                    </div>
                                                </div>
                                            </div>
                                        </Paper>
                                    </div>
                                    <DashboardDetail
                                        utcTime={this.state.dataCallingUTCTime}
                                        globalHierarchyItemId={this.state.globalHierarchyItemId}
                                        filterByPunchPermissionHierarchy={this.state.filterByPunchPermissionHierarchy}
                                        filterDirectReportsOnly={this.state.filterDirectReportsOnly}
                                        activatedTab={this.state.activatedTab}
                                        handleChangeActivatedTab={activatedTab => this.setState({ activatedTab })}
                                    />
                                    <DashboardBarChartDrawer
                                        open={this.state.drawerChartType !== null}
                                        loading={this.state.loading}
                                        chartType={this.state.drawerChartType}
                                        hierarchyData={this.state.loading ? [] : this.state.chartData.HierarchyData}
                                        handleClose={() => this.setState({ drawerChartType: null })}
                                        handleChangeActivatedTab={activatedTab => this.setState({ activatedTab })}
                                    />
                                </Paper>
                            </Tab>
                            {AccountStore.getMedicalAccount() && 
                                <Tab data-intercom-target={"menu-Dashboard-WorkforceAssessment"} label="Workforce Optimization" value="workforce">
                                    {this.state.tabActive == "workforce" && <WorkforceAssessment />}
                                </Tab>
                            }
                            {AccountStore.getTrendsEnabled() && permissionHelper.hasPermission(AuthenticationStore.Permissions.Trends.ViewTrends) &&
                                <Tab data-intercom-target={"menu-Dashboard-WorkforceAssessment"} label="Trend Analysis" value="trend">
                                    {this.state.tabActive == "trend" && <TrendsDashboard />}
                                </Tab>
                            }
                        </Tabs>
                    }
                </div>
            </div >
        )
    }
}
